import React from "react";
import SectionHeading from "@/components/ui/SectionHeading";
import useScrollReveal from "@/hooks/useScrollReveal";
import useSpotlight from "@/hooks/useSpotlight";
import { COLORS, FONTS, LAYOUT } from "@/constants/theme";

/**
 * Servicios tal como los define el one-pager institucional de ALTAMA.
 */
const SERVICES = [
  {
    step: "01",
    title: "Estrategia e inteligencia de negocio",
    tagline: "Entender mejor el mercado antes de decidir.",
    bullets: [
      "Análisis de competidores y benchmarking.",
      "Evaluación de mercado, oportunidades y riesgos.",
      "Indicadores para comparar alternativas de negocio.",
    ],
    accent: COLORS.digitalBlue,
  },
  {
    step: "02",
    title: "Analítica de datos y econometría",
    tagline: "Pasar de «qué pasó» a «por qué pasó y qué puede pasar».",
    bullets: [
      "Modelos estadísticos y econométricos aplicados al negocio.",
      "Predicción, segmentación y análisis de factores clave.",
      "Dashboards e indicadores para seguimiento ejecutivo.",
    ],
    accent: COLORS.digitalBlue,
  },
  {
    step: "03",
    title: "Inteligencia geoespacial y expansión",
    tagline: "Decidir dónde crecer con evidencia territorial.",
    bullets: [
      "Evaluación y priorización de nuevas ubicaciones.",
      "Demografía, competencia, accesibilidad y movilidad.",
      "Potencial de ventas, áreas de influencia y canibalización.",
    ],
    accent: COLORS.webOrange,
  },
  {
    step: "04",
    title: "Investigación y diseño metodológico",
    tagline: "Construir estudios que realmente respondan la pregunta correcta.",
    bullets: [
      "Diseño de estudios de mercado y competencia.",
      "Instrumentos para consumidor, marca y percepción.",
      "Metodologías, muestras, variables e indicadores.",
    ],
    accent: COLORS.webOrange,
  },
  {
    step: "05",
    title: "Soluciones digitales para procesos internos",
    tagline: "Herramientas concretas para eliminar fricción y mejorar el control.",
    bullets: [
      "Automatizar flujos y tareas manuales.",
      "Centralizar datos, evidencias y seguimiento.",
      "Monitorear operación y resultados en tiempo real.",
    ],
    accent: COLORS.digitalBlue,
  },
];

/**
 * Viñeta con marcador propio.
 *
 * @param {{ children: React.ReactNode, color: string }} props
 * @returns {JSX.Element}
 */
const Bullet = ({ children, color }) => (
  <li className="flex items-start gap-3">
    <span
      aria-hidden="true"
      className="shrink-0"
      style={{
        width: "5px",
        height: "5px",
        marginTop: "0.6rem",
        borderRadius: "50%",
        backgroundColor: color,
      }}
    />
    <span className="text-[0.96rem]" style={{ color: COLORS.lightMuted, fontFamily: FONTS.body, lineHeight: 1.6 }}>
      {children}
    </span>
  </li>
);

/**
 * Tarjeta de servicio. Es un botón: al pulsarla queda marcada y el formulario
 * de contacto se rellena con ese servicio, así el clic lleva a algo en vez de
 * abrir un HTML suelto.
 *
 * @param {{
 *   service: object,
 *   elegido: boolean,
 *   onElegir: (titulo: string) => void
 * }} props
 * @returns {JSX.Element}
 */
const ServiceCard = ({ service, elegido, onElegir, anchoCompleto = false }) => {
  const foco = useSpotlight();

  // El clic del botón burbujea hasta aquí, así que toda la tarjeta responde
  // sin duplicar manejadores ni romper el teclado.
  return (
    <article
      className={`spotlight flex h-full flex-col rounded-lg border p-8 text-left${
        anchoCompleto ? " md:col-span-2" : ""
      }`}
      onClick={() => onElegir(service.title)}
      {...foco.props}
      data-elegido={elegido ? "si" : "no"}
      style={{
        backgroundColor: COLORS.lightSurface,
        borderColor: COLORS.lightBorder,
        borderTop: `3px solid ${service.accent}`,
        cursor: "pointer",
      }}
    >
      <div className="flex w-full items-center justify-between">
        <span
          className="text-[0.82rem]"
          style={{ color: service.accent, fontFamily: FONTS.data, letterSpacing: "0.1em" }}
        >
          {service.step}
        </span>

        <span
          className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[0.68rem] uppercase"
          style={{
            border: `1px solid ${elegido ? COLORS.digitalBlue : "transparent"}`,
            backgroundColor: elegido ? "rgba(0,104,255,0.1)" : "transparent",
            color: elegido ? COLORS.digitalBlue : "transparent",
            fontFamily: FONTS.data,
            letterSpacing: "0.1em",
            transition: "color 200ms ease, background-color 200ms ease, border-color 200ms ease",
          }}
        >
          <svg aria-hidden="true" fill="none" height="10" viewBox="0 0 12 12" width="10">
            <path d="M2 6.5 4.8 9 10 3" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
          </svg>
          Elegido
        </span>
      </div>

      <h3
        className="mt-4 text-[1.3rem] md:text-[1.5rem]"
        style={{ color: COLORS.lightText, fontFamily: FONTS.display, lineHeight: 1.25, fontWeight: 600 }}
      >
        {service.title}
      </h3>

      <p
        className="mt-2 text-[1rem]"
        style={{ color: COLORS.lightMuted, fontFamily: FONTS.body, fontStyle: "italic", lineHeight: 1.6 }}
      >
        {service.tagline}
      </p>

      <ul
        className={`mt-5 ${anchoCompleto ? "grid gap-2.5 sm:grid-cols-3" : "space-y-2.5"}`}
        style={{ listStyle: "none", margin: "1.25rem 0 0", padding: 0 }}
      >
        {service.bullets.map((bullet) => (
          <Bullet color={service.accent} key={bullet}>
            {bullet}
          </Bullet>
        ))}
      </ul>

      <button
        aria-pressed={elegido}
        className="mt-auto inline-flex items-center gap-2 self-start pt-6 text-[0.9rem]"
        style={{
          background: "none",
          border: "none",
          padding: "1.5rem 0 0",
          color: elegido ? COLORS.digitalBlue : COLORS.lightMuted,
          fontFamily: FONTS.heading,
          fontWeight: 500,
          cursor: "pointer",
          transition: "color 200ms ease",
        }}
        type="button"
      >
        {elegido ? "Añadido a tu consulta" : "Me interesa este servicio"}
        <svg aria-hidden="true" fill="none" height="14" viewBox="0 0 16 16" width="14">
          <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.6" />
        </svg>
      </button>
    </article>
  );
};

/**
 * Sección de servicios y capacidades de ALTAMA CONSULTING.
 *
 * @param {{
 *   serviciosElegidos: string[],
 *   onAlternarServicio: (titulo: string) => void
 * }} props
 * @returns {JSX.Element}
 */
const ServicesSection = ({ serviciosElegidos = [], onAlternarServicio }) => {
  const [headingRef, headingVisible] = useScrollReveal(0.2);
  const [cardsRef, cardsVisible] = useScrollReveal(0.1);

  return (
    <section
      className="px-6 py-24 md:py-32"
      id="servicios"
      style={{ backgroundColor: COLORS.lightSection, scrollMarginTop: LAYOUT.scrollOffset }}
    >
      {/* -- ServicesSection -- */}
      <div className="mx-auto" style={{ maxWidth: LAYOUT.maxWidth }}>
        <div className={`reveal ${headingVisible ? "visible" : ""}`} ref={headingRef}>
          <SectionHeading
            align="left"
            lineColor={COLORS.digitalBlue}
            subtitle="Convertimos preguntas de negocio en análisis, modelos y herramientas accionables. Marca los que te interesen y los llevamos a la conversación."
            subtitleColor={COLORS.lightMuted}
            title="Cómo podemos ayudarte"
            titleColor={COLORS.lightText}
          />
        </div>

        <div
          className={`reveal-stagger ${cardsVisible ? "visible" : ""} mt-14 grid grid-cols-1 gap-6 md:grid-cols-2`}
          ref={cardsRef}
        >
          {SERVICES.map((service) => (
            <ServiceCard
              anchoCompleto={service.step === "05"}
              elegido={serviciosElegidos.includes(service.title)}
              key={service.step}
              onElegir={onAlternarServicio}
              service={service}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
